import { useTranslation } from 'react-i18next';

import { formatInstant, formatLongDate } from 'scenes/RouteReport/utils/time';

import { VISIT_TEXT_COLORS } from '../utils/charts';
import { joinParts } from '../utils/format';
import { MapPoint, RouteExecution } from '../utils/mapData';

const I18N_BASE = 'scenes.visit_history.map.info';

interface VisitPinInfoProps {
  point: MapPoint;
  execution: RouteExecution;
  timeZone: string;
}

/**
 * Content of the InfoWindow of a pin. Everything comes from the stop already
 * loaded: opening it never makes a request.
 */
function VisitPinInfo({ point, execution, timeZone }: VisitPinInfoProps) {
  const { t } = useTranslation();
  const { stop } = point;

  // `label` is the local name as the route carries it.
  const title = stop.store.label ?? stop.store.name ?? stop.store.code;
  const place = [stop.store.branchExternalId, stop.store.address]
    .filter(Boolean)
    .join(' · ');
  // An incident or a completed stop is told by its finish time; a stop that
  // is still open by the time it started, if it did.
  const at = stop.finishedAt ?? stop.startedAt;
  const time = at ? formatInstant(at, timeZone, 'HH:mm') : null;

  return (
    <div className="visit-history-pin-info">
      <div className="visit-history-pin-info__title">{title}</div>
      {place && <div className="visit-history-pin-info__muted">{place}</div>}

      <div className="visit-history-pin-info__row">
        {joinParts([
          execution.routeCode,
          execution.zoneName,
          execution.vanName,
          formatLongDate(execution.date),
          t(`${I18N_BASE}.stop_number`, { number: point.number }),
        ])}
      </div>

      <div
        className="visit-history-pin-info__state"
        style={{ color: VISIT_TEXT_COLORS[point.state] }}
      >
        {[t(`${I18N_BASE}.state.${point.state}`), time]
          .filter(Boolean)
          .join(' · ')}
      </div>

      {stop.incident && (
        <div className="visit-history-pin-info__row">
          <b>{t(`${I18N_BASE}.reason`)}:</b> {stop.incident.reason}
          {stop.incident.note && (
            <div className="visit-history-pin-info__muted">
              {stop.incident.note}
            </div>
          )}
        </div>
      )}

      <div className="visit-history-pin-info__row">
        <b>{t(`${I18N_BASE}.materials`)}</b>
        {stop.materials.length > 0 ? (
          <ul className="visit-history-pin-info__materials">
            {stop.materials.map((material) => (
              <li key={material.materialId}>
                {material.name} × {material.units}
              </li>
            ))}
          </ul>
        ) : (
          <div>—</div>
        )}
      </div>
    </div>
  );
}

export default VisitPinInfo;
